import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {PageableParams} from '@core/models/pageable-params';
import {Observable} from 'rxjs';
import {PageResponse} from '@core/models/page-response';
import {
  Ocorrencia,
  ParamsBloqueioAutomaticoOcorrencia,
  PayloadAlterarSituacao,
  PayloadBloquearUnicaOcorrencia,
  PayloadOcorrencia
} from '@core/models/ocorrencias';
import {EnumResponse} from '@core/models/enum-response';
import {ResumoOcorrencia} from '@core/models/resumo-ocorrencia';
import {Providencia} from '@core/models/providencia';
import {Historico} from '@core/models/historico';

@Injectable({
  providedIn: 'root'
})
export class OcorrenciasService {
  private readonly uri = environment.api.uri.concat('ocorrencia');
  private readonly http = inject(HttpClient)

  getServerTimeNow(): Observable<{ datetime: string }> {
    return this.http.get<{ datetime: string }>(environment.api.uri.concat('time/now'));
  }

  get(payload: PageableParams): Observable<PageResponse<Ocorrencia>> {
    const params = payload.toQueryString();
    return this.http.get<PageResponse<Ocorrencia>>(`${this.uri}?${params}`);
  }

  getById(id: number): Observable<Ocorrencia> {
    return this.http.get<Ocorrencia>(`${this.uri}/${id}`);
  }

  getProvidenciaById(id: number): Observable<Providencia> {
    return this.http.get<Providencia>(`${this.uri}/providencia/${id}`);
  }


  createProvidencia(payload: Providencia): Observable<number> {
    return this.http.post<number>(`${this.uri}/providencia`, payload);
  }

  create(payload: PayloadOcorrencia): Observable<Ocorrencia> {
    return this.http.post<Ocorrencia>(this.uri, payload);
  }

  put(id: number, payload: PayloadOcorrencia): Observable<Ocorrencia> {
    return this.http.put<Ocorrencia>(`${this.uri}/${id}`, payload);
  }

  putBloqueioUnico(id: number, payload: PayloadBloquearUnicaOcorrencia) {
    return this.http.put<void>(`${this.uri}/${id}/bloquear-desbloquear`, payload);
  }

  putEncerrarOcorrencia(id: number, payload: PayloadAlterarSituacao) {
    return this.http.put<void>(`${this.uri}/${id}/encerrar`, payload);
  }

  putInabilitarOcorrencia(id: number, payload: PayloadAlterarSituacao) {
    return this.http.put<void>(`${this.uri}/${id}/inabilitar`, payload);
  }

  putAlterarSituacaoOcorrencia(id: number, payload: PayloadAlterarSituacao) {
    return this.http.put<void>(`${this.uri}/${id}/alterar-situacao`, payload);
  }

  createBloqueioAutomatico(payload: ParamsBloqueioAutomaticoOcorrencia) {
    let params = new HttpParams();

    if (payload) {
      params = params.set('dataVigencia', String(payload.dataVigencia));
      params = params.set('quantidadeDias', String(payload.quantidadeDias));
    }

    return this.http.post<EnumResponse[]>(
      `${this.uri}/cadastrar-regra-bloqueio`,
      null,
      {params}
    );
  }

  saveHistorico(idOcorrencia: number, request: Historico): Observable<void> {
    return this.http.put<void>(`${this.uri}/${idOcorrencia}/salvar-historico`, request);
  }

  getHistoricoByOcorrencia(idOcorrencia: number): Observable<Historico> {
    return this.http.get<Historico>(`${this.uri}/${idOcorrencia}/buscar-historico-salvo`);
  }


  getResumos(idOcorrencia: number, order: 'ASC' | 'DESC' = 'DESC'): Observable<ResumoOcorrencia[]> {
    const params = new HttpParams().set('order', order);
    return this.http.get<ResumoOcorrencia[]>(`${this.uri}/${idOcorrencia}/resumo`, {params});
  }

  editarTipoOcorrencia(ocorrenciaId: number, tipoOcorrenciaId: number, justificativa: string): Observable<number> {
    return this.http.post<number>(`${this.uri}/copiar`, {ocorrenciaId, tipoOcorrenciaId, justificativa});
  }

  exportarPdf(idOcorrencia: number): Observable<Blob> {
    return this.http.get(`${this.uri}/gerar-pdf`, {
      params: {idOcorrencia},
      responseType: 'blob'
    });
  }

}
